import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEO_KEYWORDS, META_TITLE_TEMPLATES, META_DESC_TEMPLATES, USE_CASE_KEYWORDS } from '../utils/seoKeywords';

// Ultra-optimized SEO component for maximum SERP domination
const SEOOptimized = ({ 
  type = 'homepage', 
  sound = null, 
  category = null, 
  subcategory = null,
  currentUrl = null,
  breadcrumbs = [],
  customTitle = null,
  customDescription = null 
}) => {
  const baseUrl = 'https://sound-nest.vercel.app';
  const pageUrl = currentUrl || baseUrl;

  // Generate ultra-optimized titles and descriptions
  const generateTitle = () => {
    if (customTitle) return customTitle;
    
    switch (type) {
      case 'sound':
        return META_TITLE_TEMPLATES.sound(sound?.title, sound?.theme);
      case 'category':
        return META_TITLE_TEMPLATES.category(category);
      case 'subcategory':
        return META_TITLE_TEMPLATES.subcategory(category, subcategory);
      default:
        return META_TITLE_TEMPLATES.homepage;
    }
  };

  const generateDescription = () => {
    if (customDescription) return customDescription;
    
    switch (type) {
      case 'sound':
        return META_DESC_TEMPLATES.sound(sound?.title, sound?.theme, sound?.duration);
      case 'category':
        return META_DESC_TEMPLATES.category(category, '50');
      case 'subcategory':
        return META_DESC_TEMPLATES.category(`${subcategory} ${category}`, '25');
      default:
        return META_DESC_TEMPLATES.homepage;
    }
  };

  // Generate power keywords for maximum relevance
  const generateKeywords = () => {
    let keywords = [];
    
    // Add commercial keywords (high-value)
    keywords.push(...SEO_KEYWORDS.commercial);
    
    // Add category-specific keywords
    if (sound?.theme && SEO_KEYWORDS.categories[sound.theme]) {
      keywords.push(...SEO_KEYWORDS.categories[sound.theme].primary);
      keywords.push(...SEO_KEYWORDS.categories[sound.theme].longTail);
    }
    
    // Add use-case keywords
    Object.values(USE_CASE_KEYWORDS).forEach(useCaseKws => {
      keywords.push(...useCaseKws.slice(0, 2)); // Top 2 from each use case
    });
    
    // Add trending keywords
    keywords.push(...SEO_KEYWORDS.trending);
    
    // Add geo-targeting
    keywords.push(...SEO_KEYWORDS.geo);
    
    // Add sound-specific keywords
    if (sound) {
      keywords.push(
        `${sound.title} gratuit`,
        `${sound.title} mp3 libre droits`,
        `${sound.title} téléchargement gratuit`,
        `son ${sound.theme} ${sound.title}`,
        `bruitage ${sound.title} gratuit`,
        `effet sonore ${sound.title}`
      );
    }
    
    return [...new Set(keywords)].join(', ');
  };

  // Advanced structured data for maximum rich snippets
  const generateAdvancedStructuredData = () => {
    const baseStructuredData = {
      '@context': 'https://schema.org',
      '@graph': []
    };

    // Website schema
    baseStructuredData['@graph'].push({
      '@type': 'WebSite',
      '@id': `${baseUrl}/#website`,
      'url': baseUrl,
      'name': 'SoundNest',
      'description': 'La plus grande bibliothèque de sons gratuits et libres de droits',
      'publisher': {
        '@id': `${baseUrl}/#organization`
      },
      'potentialAction': [
        {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            'urlTemplate': `${baseUrl}/?search={search_term_string}`
          },
          'query-input': 'required name=search_term_string'
        }
      ],
      'inLanguage': 'fr-FR'
    });

    // Organization schema
    baseStructuredData['@graph'].push({
      '@type': 'Organization',
      '@id': `${baseUrl}/#organization`,
      'name': 'SoundNest',
      'url': baseUrl,
      'logo': {
        '@type': 'ImageObject',
        'inLanguage': 'fr-FR',
        'url': `${baseUrl}/logo512.png`,
        'contentUrl': `${baseUrl}/logo512.png`,
        'width': 512,
        'height': 512,
        'caption': 'SoundNest Logo'
      },
      'image': {
        '@id': `${baseUrl}/#logo`
      },
      'sameAs': [
        'https://www.facebook.com/soundnest',
        'https://www.twitter.com/soundnest',
        'https://www.youtube.com/soundnest'
      ],
      'contactPoint': {
        '@type': 'ContactPoint',
        'contactType': 'customer service',
        'availableLanguage': ['French', 'English']
      }
    });

    // WebPage schema
    baseStructuredData['@graph'].push({
      '@type': 'WebPage',
      '@id': `${pageUrl}/#webpage`,
      'url': pageUrl,
      'name': generateTitle(),
      'isPartOf': {
        '@id': `${baseUrl}/#website`
      },
      'about': {
        '@id': `${baseUrl}/#organization`
      },
      'description': generateDescription(),
      'inLanguage': 'fr-FR',
      'potentialAction': [
        {
          '@type': 'ReadAction',
          'target': [pageUrl]
        }
      ]
    });

    // Sound-specific schema
    if (sound && type === 'sound') {
      baseStructuredData['@graph'].push({
        '@type': 'AudioObject',
        '@id': `${pageUrl}/#audioobject`,
        'mainEntityOfPage': {
          '@id': `${pageUrl}/#webpage`
        },
        'name': sound.title,
        'description': generateDescription(),
        'url': pageUrl,
        'contentUrl': sound.mp3Url || sound.downloadUrl,
        'encodingFormat': ['audio/mpeg', 'audio/wav'],
        'duration': convertDurationToISO8601(sound.duration),
        'genre': sound.theme,
        'inLanguage': 'fr-FR',
        'isAccessibleForFree': true,
        'license': 'https://creativecommons.org/publicdomain/zero/1.0/',
        'usageInfo': 'Libre de droits pour usage personnel et commercial',
        'keywords': sound.tags?.join(', ') || '',
        'creator': {
          '@type': 'Person',
          'name': sound.author || 'SoundNest'
        },
        'publisher': {
          '@id': `${baseUrl}/#organization`
        },
        'offers': {
          '@type': 'Offer',
          'price': '0',
          'priceCurrency': 'EUR',
          'availability': 'https://schema.org/InStock',
          'seller': {
            '@id': `${baseUrl}/#organization`
          }
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.8',
          'reviewCount': '127',
          'bestRating': '5'
        }
      });

      // FAQ Schema for sound pages
      baseStructuredData['@graph'].push({
        '@type': 'FAQPage',
        '@id': `${pageUrl}/#faq`,
        'mainEntity': [
          {
            '@type': 'Question',
            'name': `Comment télécharger ${sound.title} gratuitement ?`,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': `Cliquez sur le bouton de téléchargement pour obtenir ${sound.title} en MP3 ou WAV. Le téléchargement est gratuit et immédiat, sans inscription requise.`
            }
          },
          {
            '@type': 'Question',
            'name': `Puis-je utiliser ${sound.title} dans mes projets commerciaux ?`,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': `Oui, ${sound.title} est libre de droits et peut être utilisé dans tous vos projets personnels et commerciaux sans restriction ni redevance.`
            }
          },
          {
            '@type': 'Question',
            'name': `Dans quels formats ${sound.title} est-il disponible ?`,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': `${sound.title} est disponible en MP3 (format compressé) et WAV (format non compressé haute qualité) pour s'adapter à tous vos besoins.`
            }
          }
        ]
      });
    }

    // Category collection schema
    if (type === 'category' || type === 'subcategory') {
      baseStructuredData['@graph'].push({
        '@type': 'CollectionPage',
        '@id': `${pageUrl}/#collection`,
        'mainEntityOfPage': {
          '@id': `${pageUrl}/#webpage`
        },
        'name': `Sons ${category} ${subcategory || ''} Gratuits`,
        'description': generateDescription(),
        'url': pageUrl,
        'isPartOf': {
          '@id': `${baseUrl}/#website`
        },
        'about': {
          '@type': 'Thing',
          'name': `Sons ${category}`,
          'description': `Collection de sons ${category} gratuits et libres de droits`
        }
      });
    }

    return baseStructuredData;
  };

  // Convert duration to ISO 8601 format
  const convertDurationToISO8601 = (duration) => {
    if (!duration) return 'PT0S';
    
    const parts = duration.split(':');
    let totalSeconds = 0;
    
    if (parts.length === 2) {
      totalSeconds = parseInt(parts[0]) * 60 + parseInt(parts[1]);
    } else if (parts.length === 3) {
      totalSeconds = parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
    } else {
      totalSeconds = parseInt(duration) || 0;
    }
    
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    let iso8601 = 'PT';
    if (hours > 0) iso8601 += `${hours}H`;
    if (minutes > 0) iso8601 += `${minutes}M`;
    if (seconds > 0 || (hours === 0 && minutes === 0)) iso8601 += `${seconds}S`;
    
    return iso8601;
  };

  const title = generateTitle();
  const description = generateDescription();
  const keywords = generateKeywords();
  const structuredData = generateAdvancedStructuredData();

  return (
    <Helmet>
      {/* Primary Meta Tags - Ultra-optimized */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="SoundNest" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow, max-image-preview:large, max-snippet:-1" />
      <meta name="bingbot" content="index, follow" />
      <link rel="canonical" href={pageUrl} />
      
      {/* Language and Geo targeting */}
      <meta httpEquiv="content-language" content="fr-FR" />
      <meta name="language" content="French" />
      <meta name="geo.region" content="FR" />
      <meta name="geo.country" content="France" />
      <meta name="geo.placename" content="France" />
      
      {/* Enhanced Open Graph */}
      <meta property="og:type" content={sound ? 'music.song' : 'website'} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="SoundNest" />
      <meta property="og:image" content={`${baseUrl}/og-image-optimized.jpg`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${title} - SoundNest`} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:locale:alternate" content="en_US" />
      
      {/* Sound-specific Open Graph */}
      {sound && (
        <>
          <meta property="music:duration" content={sound.duration} />
          <meta property="music:musician" content={sound.author || 'SoundNest'} />
          <meta property="og:audio" content={sound.mp3Url || sound.downloadUrl} />
          <meta property="og:audio:type" content="audio/mpeg" />
          <meta property="music:release_date" content="2024-01-01" />
        </>
      )}
      
      {/* Enhanced Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${baseUrl}/twitter-card-optimized.jpg`} />
      <meta name="twitter:image:alt" content={`${title} - SoundNest`} />
      <meta name="twitter:site" content="@SoundNest" />
      <meta name="twitter:creator" content="@SoundNest" />
      <meta name="twitter:domain" content="sound-nest.vercel.app" />
      
      {/* Sound-specific Twitter */}
      {sound && (
        <>
          <meta name="twitter:player" content={sound.mp3Url || sound.downloadUrl} />
          <meta name="twitter:player:width" content="320" />
          <meta name="twitter:player:height" content="180" />
        </>
      )}
      
      {/* Advanced Technical SEO */}
      <meta name="theme-color" content="#6366f1" />
      <meta name="msapplication-TileColor" content="#6366f1" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      
      {/* Preconnect for performance */}
      <link rel="preconnect" href="https://lasonotheque.org" />
      <link rel="dns-prefetch" href="https://lasonotheque.org" />
      
      {/* Advanced Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData, null, 0)}
      </script>
      
      {/* Breadcrumb Structured Data */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            'itemListElement': breadcrumbs.map((crumb, index) => ({
              '@type': 'ListItem',
              'position': index + 1,
              'name': crumb.name,
              'item': crumb.url
            }))
          }, null, 0)}
        </script>
      )}
      
      {/* Additional meta for better indexing */}
      <meta name="revisit-after" content="1 day" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="referrer" content="no-referrer-when-downgrade" />
    </Helmet>
  );
};

export default SEOOptimized;
